import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { getServiceNowClients } from '../../api/servicenow-lookups.js';
import type { SNClientOption } from '../../db/SNLookups.js';

/**
 * Module-level cache so re-mounting rows (e.g. when navigating between detail views in
 * the ERM) doesn't cause every cell to flash a loading spinner. The SN client list is
 * small (a few dozen rows) and rarely changes during a session — one fetch per page load
 * is plenty.
 */
let cached: SNClientOption[] | null = null;
let pending: Promise<SNClientOption[]> | null = null;

async function loadClientsOnce(): Promise<SNClientOption[]> {
  if (cached) return cached;
  if (pending) return pending;
  pending = (async () => {
    const list = (await getServiceNowClients()({ id: 1, name: 1, code: 1 })) as SNClientOption[];
    cached = list;
    return list;
  })();
  try {
    return await pending;
  } finally {
    pending = null;
  }
}

/**
 * Resolves a ServiceNow client sys_id to its human-friendly name (and code, when present).
 * Falls back to a truncated sys_id while the lookup loads or when no match is found —
 * either case is rare but we render *something* so the column never goes blank.
 */
export const ServiceNowCustomerName = stateful(({ clientId }: { clientId?: string | null }) => {
  if (!clientId) {
    return html`<span class="text-base-content/40">—</span>`;
  }

  const [clients, setClients] = useState<SNClientOption[] | null>(cached);

  useEffect(() => {
    if (clients) return;
    void loadClientsOnce().then(setClients);
  }, []);

  if (!clients) {
    return html`<span class="text-xs font-mono text-base-content/40">${clientId.slice(0, 8)}…</span>`;
  }

  const match = clients.find(c => c.id === clientId);

  if (!match) {
    return html`<span class="text-xs font-mono text-base-content/50" title="Unknown SN client (sys_id only)"
      >${clientId.slice(0, 8)}…</span
    >`;
  }

  return html`
    <div class="flex flex-col">
      <span class="text-sm">${match.name ?? clientId}</span>
      ${match.code ? html`<span class="text-xs text-base-content/50">${match.code}</span>` : nothing}
    </div>
  `;
});
